import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { SIDEBAR_WIDTH, useUi } from '../stores/ui';

const KEY_STEP = 16;

/**
 * Drag the history sidebar's right edge to make it wider or narrower (desktop only; the CSS
 * hides the handle on phones). The width is remembered. Arrow keys move it too, Home and End
 * go to the narrowest and widest, and a double click or Enter goes back to the default.
 */
export function SidebarResizeHandle() {
  const t = useT();
  const width = useUi((state) => state.sidebarWidth);
  const setWidth = useUi((state) => state.setSidebarWidth);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);
  const current = width ?? SIDEBAR_WIDTH.default;

  useEffect(() => {
    const root = document.documentElement;
    if (width === null) root.style.removeProperty('--nav-w');
    else root.style.setProperty('--nav-w', `${width}px`);
  }, [width]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: a focusable separator is the ARIA pattern for a resize handle; <hr> cannot take focus or keys.
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('resizeSidebar')}
      aria-valuemin={SIDEBAR_WIDTH.min}
      aria-valuemax={SIDEBAR_WIDTH.max}
      aria-valuenow={current}
      tabIndex={0}
      title={t('resizeSidebar')}
      className="nv-navresize"
      data-dragging={dragging}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const aside = event.currentTarget.parentElement;
        start.current = {
          x: event.clientX,
          width: aside?.getBoundingClientRect().width || current,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (!start.current) return;
        setWidth(start.current.width + event.clientX - start.current.x);
      }}
      onPointerUp={(event) => {
        start.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        setDragging(false);
      }}
      onPointerCancel={() => {
        start.current = null;
        setDragging(false);
      }}
      onDoubleClick={() => setWidth(null)}
      onKeyDown={(event) => {
        const next =
          event.key === 'ArrowLeft'
            ? current - KEY_STEP
            : event.key === 'ArrowRight'
              ? current + KEY_STEP
              : event.key === 'Home'
                ? SIDEBAR_WIDTH.min
                : event.key === 'End'
                  ? SIDEBAR_WIDTH.max
                  : undefined;
        if (event.key === 'Enter') {
          event.preventDefault();
          setWidth(null);
          return;
        }
        if (next === undefined) return;
        event.preventDefault();
        setWidth(next);
      }}
    />
  );
}
